/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import {
    forgotPassword as AuthServiceForgotPassword,
    login as AuthServiceLogin,
    logout as AuthServiceLogout,
} from 'common/services/AuthService';
import Alert from 'common/components/Alert';
import { ServiceWorkerContext } from 'useServiceWorker';
import { UserTokenContext } from 'useUserToken';
import { startTour } from 'common/guide-tour';
import { initStoreAfterLogin } from 'common/stores/redux/store';
import {
    resetAuthentication,
    setAuthentication,
    setLoading,
} from 'common/stores/redux/Authentication';

export function useAuthActions() {
    const serviceWorker: any = useContext(ServiceWorkerContext);
    const userToken: any = useContext(UserTokenContext);
    const { updateServiceWorker } = serviceWorker;
    const { setLoggedIn } = userToken;
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const processLogin = async (email: string, password: string) => {
        dispatch(setLoading(true));
        try {
            // check if new version available
            const { updateServiceWorker } = serviceWorker;
            const { setLoggedIn } = userToken;
            await setLoggedIn(false);
            const loginResult = await AuthServiceLogin({
                email: email.toLowerCase().trim(),
                password,
            });
            if (loginResult.changePassword === true) {
                dispatch(setLoading(false));

                navigate('/updatePassword');
            } else {
                // call service worker update after login success
                if (updateServiceWorker) {
                    // update service worker
                    updateServiceWorker(true);
                }

                dispatch(setAuthentication(loginResult.token));

                await setLoggedIn(true);
                initStoreAfterLogin();
                // start tour after logged in
                dispatch(startTour());
            }
        } catch (err) {
            dispatch(resetAuthentication());

            handleErrorAlert(err);
        }
    };

    const processLogout = async () => {
        try {
            await setLoggedIn(false);
            if (updateServiceWorker) {
                // call service worker update after logout
                await updateServiceWorker(true);
            }

            AuthServiceLogout({ noRedirect: true });
        } catch (error) {
            dispatch(resetAuthentication());
            Alert.error(
                'There was an error processing the request, please contact support@motili.com'
            );

            throw error;
        }
    };

    const processForgotPassword = async (forgotPasswordEmail: string) => {
        try {
            const response = await AuthServiceForgotPassword({
                email: forgotPasswordEmail,
            });
            dispatch(resetAuthentication());
            Alert.success(
                'Password reset instructions have been sent to your email address.',
                {
                    autoClose: 3000,
                }
            );

            return response;
        } catch (error) {
            dispatch(resetAuthentication());
            handleErrorAlert(error);

            return error;
        }
    };

    return { processLogin, processLogout, processForgotPassword };
}

function handleErrorAlert(errObject: any) {
    if (errObject?.statusCode === 401) {
        return Alert.error(
            'The provided email and password were not recognized, please try again. Please contact support@motili.com if you need assistance logging into your account.',
            {
                autoClose: 3000,
            }
        );
    }
    return Alert.error(
        'There was an error processing the request, please contact support@motili.com if you need assistance logging into your account.',
        {
            autoClose: 3000,
        }
    );
}
